import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  CalendarIcon, ShieldCheckIcon, AlertTriangleIcon, ClockIcon,
  PlusIcon, CheckCircleIcon, XCircleIcon, RefreshCwIcon, GitMergeIcon,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { getApiEndpoint } from "@/lib/api";
import { getToken, getUser } from "@/lib/auth-storage";
import { useToast } from "@/hooks/use-toast";

interface ChangeWindow {
  id: number;
  title: string;
  description?: string;
  window_type: string;
  start_at: string;
  end_at: string;
  recurrence: string;
  created_by_name?: string;
}

interface ChangeTicket {
  id: string;
  title: string;
  priority: string;
  status: string;
  tracking_status?: string;
  change_category?: string;
  risk_level?: string;
  cab_meeting_date?: string;
  deployed_at?: string;
  created_at: string;
  requester_name?: string;
}

const CATEGORY_LABELS: Record<string, string> = {
  standard: "Padrão", normal: "Normal", emergency: "Emergência",
};
const RISK_COLORS: Record<string, string> = {
  low: "text-green-600 bg-green-50", medium: "text-yellow-600 bg-yellow-50",
  high: "text-orange-600 bg-orange-50", critical: "text-red-600 bg-red-50",
};
const CATEGORY_COLORS: Record<string, string> = {
  standard: "text-blue-600 bg-blue-50",
  normal: "text-purple-600 bg-purple-50",
  emergency: "text-red-600 bg-red-50",
};

const fmtDate = (iso?: string) => iso ? new Date(iso).toLocaleDateString("pt-BR", { day:"2-digit", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" }) : "—";

const ChangeManagement = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [windows, setWindows] = useState<ChangeWindow[]>([]);
  const [changes, setChanges] = useState<ChangeTicket[]>([]);
  const [calWindows, setCalWindows] = useState<ChangeWindow[]>([]);
  const [calChanges, setCalChanges] = useState<ChangeTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewWindow, setShowNewWindow] = useState(false);
  const [calMonth, setCalMonth] = useState(new Date().getMonth() + 1);
  const [calYear, setCalYear] = useState(new Date().getFullYear());
  const [newWin, setNewWin] = useState({
    title: "", description: "", window_type: "standard",
    start_at: "", end_at: "", recurrence: "none",
  });

  const token = getToken();
  const user = getUser() ? JSON.parse(getUser()!) : null;
  const isInternal = user && (user.role === "admin" || user.role === "internal" || user.role === "analyst");

  useEffect(() => {
    if (!isInternal) { navigate("/"); return; }
    loadAll();
  }, []);

  useEffect(() => {
    loadCalendar();
  }, [calMonth, calYear]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [wRes, cRes] = await Promise.all([
        fetch(getApiEndpoint("/api/changes/windows"), { headers: { Authorization: `Bearer ${token}` } }),
        fetch(getApiEndpoint("/api/tickets?type=change&limit=50"), { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (wRes.ok) { const d = await wRes.json(); setWindows(d.change_windows || []); }
      if (cRes.ok) { const d = await cRes.json(); setChanges(d.tickets || []); }
    } finally {
      setLoading(false);
    }
  }, [token]);

  const loadCalendar = useCallback(async () => {
    const res = await fetch(
      getApiEndpoint(`/api/changes/calendar?month=${calMonth}&year=${calYear}`),
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (res.ok) {
      const d = await res.json();
      setCalWindows(d.change_windows || []);
      setCalChanges(d.changes || []);
    }
  }, [token, calMonth, calYear]);

  const handleCreateWindow = async () => {
    if (!newWin.title || !newWin.start_at || !newWin.end_at) {
      toast({ title: "Campos obrigatórios", description: "Título, início e fim são necessários", variant: "destructive" });
      return;
    }
    const res = await fetch(getApiEndpoint("/api/changes/windows"), {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(newWin),
    });
    if (res.ok) {
      toast({ title: "Janela criada", description: `Janela "${newWin.title}" criada` });
      setShowNewWindow(false);
      setNewWin({ title: "", description: "", window_type: "standard", start_at: "", end_at: "", recurrence: "none" });
      loadAll(); loadCalendar();
    } else {
      toast({ title: "Erro", description: "Não foi possível criar a janela", variant: "destructive" });
    }
  };

  const prevMonth = () => {
    if (calMonth === 1) { setCalMonth(12); setCalYear(y => y - 1); }
    else setCalMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (calMonth === 12) { setCalMonth(1); setCalYear(y => y + 1); }
    else setCalMonth(m => m + 1);
  };

  const monthName = new Date(calYear, calMonth - 1).toLocaleDateString("pt-BR", { month: "long", year: "numeric" });

  const pendingChanges = changes.filter(c => !c.deployed_at && c.status !== "closed" && c.status !== "resolved");
  const deployedChanges = changes.filter(c => c.deployed_at);

  if (!isInternal) return null;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex flex-col">
      <Header />
      <main className="flex-1 w-full max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <GitMergeIcon className="h-6 w-6 text-purple-600" /> Gestão de Mudanças
            </h1>
            <p className="text-gray-500 text-sm mt-1">CAB, Janelas de Mudança e Aprovações</p>
          </div>
          <Button onClick={loadAll} variant="outline" size="sm" className="gap-2">
            <RefreshCwIcon className="h-4 w-4" /> Atualizar
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { label: "Mudanças Pendentes", value: pendingChanges.length, icon: ClockIcon, color: "yellow" },
            { label: "Implantadas", value: deployedChanges.length, icon: CheckCircleIcon, color: "green" },
            { label: "Janelas Ativas", value: windows.length, icon: CalendarIcon, color: "blue" },
            { label: "Emergenciais", value: changes.filter(c => c.change_category === "emergency").length, icon: AlertTriangleIcon, color: "red" },
          ].map((m, i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500">{m.label}</p>
                    <p className={`text-2xl font-bold text-${m.color}-600`}>{loading ? "—" : m.value}</p>
                  </div>
                  <m.icon className={`h-8 w-8 text-${m.color}-200`} />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Tabs defaultValue="calendar">
          <TabsList className="mb-4">
            <TabsTrigger value="calendar">Calendário CAB</TabsTrigger>
            <TabsTrigger value="changes">Mudanças</TabsTrigger>
            <TabsTrigger value="windows">Janelas de Mudança</TabsTrigger>
          </TabsList>

          {/* ── Calendário ── */}
          <TabsContent value="calendar">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-3">
                <CardTitle className="text-base capitalize">{monthName}</CardTitle>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={prevMonth}>‹</Button>
                  <Button variant="outline" size="sm" onClick={() => { setCalMonth(new Date().getMonth()+1); setCalYear(new Date().getFullYear()); }}>
                    Hoje
                  </Button>
                  <Button variant="outline" size="sm" onClick={nextMonth}>›</Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {calWindows.length === 0 && calChanges.length === 0 ? (
                    <p className="text-center text-gray-400 py-8">Nenhuma janela ou mudança neste mês</p>
                  ) : (
                    <>
                      {calWindows.map(w => (
                        <div key={w.id} className="flex items-start gap-3 p-3 rounded-lg bg-blue-50 border border-blue-100">
                          <CalendarIcon className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                          <div>
                            <p className="font-medium text-sm text-blue-900">{w.title}</p>
                            <p className="text-xs text-blue-600">{fmtDate(w.start_at)} → {fmtDate(w.end_at)}</p>
                            <Badge className="mt-1 text-xs bg-blue-100 text-blue-800 border-0">
                              {w.window_type === "standard" ? "Padrão" : w.window_type === "emergency" ? "Emergência" : "Manutenção"}
                            </Badge>
                          </div>
                        </div>
                      ))}
                      {calChanges.map(c => (
                        <Link key={c.id} to={`/atendimento/chamado/${c.id}`} className="block">
                          <div className="flex items-start gap-3 p-3 rounded-lg bg-purple-50 border border-purple-100 hover:bg-purple-100 transition-colors">
                            <GitMergeIcon className="h-4 w-4 text-purple-600 mt-0.5 shrink-0" />
                            <div className="flex-1">
                              <p className="font-medium text-sm text-purple-900">{c.title}</p>
                              <p className="text-xs text-purple-600">{c.id} • CAB: {fmtDate(c.cab_meeting_date)}</p>
                            </div>
                            {c.change_category && (
                              <Badge className={`text-xs ${CATEGORY_COLORS[c.change_category] || ""} border-0`}>
                                {CATEGORY_LABELS[c.change_category]}
                              </Badge>
                            )}
                          </div>
                        </Link>
                      ))}
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── Mudanças ── */}
          <TabsContent value="changes">
            <div className="space-y-3">
              {changes.map(c => (
                <Link key={c.id} to={`/atendimento/chamado/${c.id}`} className="block">
                  <Card className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className="flex-1">
                        <p className="font-medium text-sm">{c.title}</p>
                        <p className="text-xs text-gray-500">{c.id} • {c.requester_name || "—"} • {fmtDate(c.created_at)}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {c.change_category && (
                          <Badge className={`text-xs ${CATEGORY_COLORS[c.change_category] || ""} border-0`}>
                            {CATEGORY_LABELS[c.change_category]}
                          </Badge>
                        )}
                        {c.risk_level && (
                          <Badge className={`text-xs ${RISK_COLORS[c.risk_level] || ""} border-0`}>
                            Risco {c.risk_level}
                          </Badge>
                        )}
                        {c.deployed_at ? (
                          <Badge className="text-xs bg-green-50 text-green-700 border-0">
                            <CheckCircleIcon className="h-3 w-3 mr-1" /> Implantado
                          </Badge>
                        ) : (
                          <Badge className="text-xs bg-yellow-50 text-yellow-700 border-0">
                            <ClockIcon className="h-3 w-3 mr-1" /> Pendente
                          </Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
              {!loading && changes.length === 0 && (
                <div className="text-center text-gray-400 py-12">
                  <GitMergeIcon className="h-10 w-10 mx-auto mb-2 opacity-30" />
                  <p>Nenhuma mudança registrada ainda.</p>
                  <Link to="/novo-chamado">
                    <Button className="mt-3" size="sm">
                      <PlusIcon className="h-4 w-4 mr-1" /> Abrir Mudança
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </TabsContent>

          {/* ── Janelas ── */}
          <TabsContent value="windows">
            <div className="flex justify-end mb-3">
              {(user?.role === "admin" || user?.role === "internal") && (
                <Button onClick={() => setShowNewWindow(true)} size="sm" className="gap-2">
                  <PlusIcon className="h-4 w-4" /> Nova Janela
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {windows.map(w => (
                <Card key={w.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <p className="font-medium text-sm">{w.title}</p>
                      <Badge className={`text-xs border-0 ${w.window_type === "emergency" ? "bg-red-50 text-red-700" : w.window_type === "maintenance" ? "bg-orange-50 text-orange-700" : "bg-blue-50 text-blue-700"}`}>
                        {w.window_type === "standard" ? "Padrão" : w.window_type === "emergency" ? "Emergência" : "Manutenção"}
                      </Badge>
                    </div>
                    {w.description && <p className="text-xs text-gray-500 mb-2">{w.description}</p>}
                    <div className="text-xs text-gray-500 space-y-1">
                      <div className="flex items-center gap-1"><CalendarIcon className="h-3 w-3" /> Início: {fmtDate(w.start_at)}</div>
                      <div className="flex items-center gap-1"><CalendarIcon className="h-3 w-3" /> Fim: {fmtDate(w.end_at)}</div>
                      {w.recurrence && w.recurrence !== "none" && (
                        <div className="flex items-center gap-1"><RefreshCwIcon className="h-3 w-3" /> Recorrência: {w.recurrence}</div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
              {!loading && windows.length === 0 && (
                <div className="col-span-full text-center text-gray-400 py-12">
                  <CalendarIcon className="h-10 w-10 mx-auto mb-2 opacity-30" />
                  <p>Nenhuma janela de mudança cadastrada.</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Dialog: Nova Janela */}
      <Dialog open={showNewWindow} onOpenChange={setShowNewWindow}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Nova Janela de Mudança</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium">Título *</label>
              <Input value={newWin.title} onChange={e => setNewWin(p => ({ ...p, title: e.target.value }))} placeholder="Ex: Manutenção Semanal" />
            </div>
            <div>
              <label className="text-sm font-medium">Tipo</label>
              <Select value={newWin.window_type} onValueChange={v => setNewWin(p => ({ ...p, window_type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Padrão</SelectItem>
                  <SelectItem value="emergency">Emergência</SelectItem>
                  <SelectItem value="maintenance">Manutenção</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Descrição</label>
              <Textarea value={newWin.description} onChange={e => setNewWin(p => ({ ...p, description: e.target.value }))} rows={2} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-sm font-medium">Início *</label>
                <Input type="datetime-local" value={newWin.start_at} onChange={e => setNewWin(p => ({ ...p, start_at: e.target.value }))} />
              </div>
              <div>
                <label className="text-sm font-medium">Fim *</label>
                <Input type="datetime-local" value={newWin.end_at} onChange={e => setNewWin(p => ({ ...p, end_at: e.target.value }))} />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Recorrência</label>
              <Select value={newWin.recurrence} onValueChange={v => setNewWin(p => ({ ...p, recurrence: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sem recorrência</SelectItem>
                  <SelectItem value="weekly">Semanal</SelectItem>
                  <SelectItem value="monthly">Mensal</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewWindow(false)}>Cancelar</Button>
            <Button onClick={handleCreateWindow}>Criar Janela</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default ChangeManagement;
